/**
 * STARTERS — the shipped flow library, in the editor's own rail.
 *
 * This began as a separate gallery modal in front of the canvas, which was wrong in a way
 * that only became obvious once you used it: choosing a starter is not a decision you make
 * ONCE, before you begin. It is how you explore. You want to see what "Slow, not down" looks
 * like drawn out, then look at "We already know", then go back — and a modal that closes
 * behind you makes each comparison a round trip through a dialog.
 *
 * So the list lives beside the palette, and picking one REDRAWS the canvas. Picking another
 * redraws again. That is safe because nothing on this screen is saved until Save: the phone
 * keeps answering with the last version that compiled, the read-through says so out loud,
 * and undo puts back whatever was there a moment ago.
 *
 * Each row carries its description and its reasoning, because what is being chosen is a
 * piece of writing and the reasoning is the part worth reading. "Service is out — we already
 * know" has no Page step on purpose; you cannot see that in a name.
 */
import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';

import { api } from '../../lib/api.ts';
import type { FlowTemplateCard } from '../../lib/api.ts';
import { useResource } from '../../lib/useApi.ts';
import { HelpTip } from '../HelpTip.tsx';

const CATEGORY_ORDER: FlowTemplateCard['category'][] = [
  'Trouble', 'Emergency', 'Billing', 'Sales', 'Admin',
];

export function StarterList({
  onPick,
  activeCode,
  disabled = false,
}: {
  onPick: (template: FlowTemplateCard) => void;
  activeCode: string | null;
  disabled?: boolean;
}): ReactElement | null {
  /** Static per release, so it is fetched once and kept. */
  const query = useResource(['flow-templates'], () => api.flows.templates(), { staleTimeMs: 600_000 });
  const [openCode, setOpenCode] = useState<string | null>(null);

  const groups = useMemo(() => {
    const all = query.data?.templates ?? [];
    return CATEGORY_ORDER
      .map((category) => ({ category, items: all.filter((t) => t.category === category) }))
      .filter((g) => g.items.length > 0);
  }, [query.data]);

  // Degrade quietly (doctrine 9): the palette above still works, and a missing starter list
  // is not worth an error panel on a screen somebody is mid-edit on.
  if (query.isError || groups.length === 0) return null;

  return (
    <section className="ndf-starters" aria-label="Start from a flow that already works">
      <div className="ndf-rail-head">
        START FROM ONE OF THESE
        <HelpTip helpKey="flows.starters" />
      </div>
      <p className="ndf-rail-hint">
        Draws it on the canvas so you can read it. Pick another to swap. Nothing is saved
        until you press Save.
      </p>

      {/*
        The ROWS scroll, not the section. The rail holds this list and the palette side by
        side in one column, and whichever of them scrolled as a whole took its own heading
        off the screen with it — so the person scrolling could no longer tell which of the
        two lists they were in.
      */}
      <div className="ndf-starter-scroll">
        {groups.map((g) => (
          <div key={g.category} className="ndf-starter-group">
            <div className="ndf-starter-cat">{g.category}</div>
            {g.items.map((t) => {
              const open = openCode === t.code;
              return (
                <div key={t.code} className="ndf-starter" data-active={activeCode === t.code ? 'true' : undefined}>
                  <button
                    type="button"
                    className="ndf-starter-main"
                    disabled={disabled}
                    onClick={() => onPick(t)}
                    title={disabled ? 'This flow is locked until you choose to replace it.' : `Draw “${t.name}” on the canvas`}
                  >
                    <span className="ndf-starter-name">{t.name}</span>
                    <span className="ndf-starter-desc">{t.description}</span>
                  </button>
                  <button
                    type="button"
                    className="ndf-starter-why-toggle"
                    aria-expanded={open}
                    onClick={() => setOpenCode(open ? null : t.code)}
                  >
                    {open ? 'Hide why' : 'Why this way'}
                  </button>
                  {open ? <p className="ndf-starter-why">{t.why}</p> : null}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </section>
  );
}

export default StarterList;
